// Lens colour table: one RGBA entry per lens byte (hexdata.ts setLens: scalar = round(v · 254),
// category = index, 255 = no data). Built from `LensResult.legend`, with the same rule as the
// minimap (src/ui/Minimap.tsx lensColour), so the map, the minimap and the legend agree.
//
//   byte 0 ......... 254     255
//        │            │       └─ no data: alpha 0 (the shader hatches it grey)
//        │            └─ scalar: last stop      category: index ≥ legend.length has no colour
//        └─ scalar: first stop, colours mixed in sRGB between evenly spaced stops
//
// Entries are sRGB bytes (a legend colour is an sRGB hex, so nothing is lost); the shader decodes them.
// Alpha 255 marks an entry with a colour.

import { HEX_COUNT } from '../../core/grid.ts';
import type { LensResult } from '../../world/api.ts';

/** Entries in the table: every value of a state-texture byte. */
export const LUT_SIZE = 256;
const CHANNELS = 4;
const SCALAR_MAX = 254;
const NO_DATA = 255;
const HEX_RADIX = 16;
const SHORT_HEX_LENGTH = 3;
const BYTE = 255;
const GREY = 0.5;

type Rgb = readonly [number, number, number];

/** '#rrggbb' or '#rgb' to sRGB 0..1; anything else is mid grey (a bad legend entry stays visible). */
export function parseHex(hex: string): Rgb {
  const digits = hex.replace('#', '');
  const full = digits.length === SHORT_HEX_LENGTH ? [...digits].map((d) => d + d).join('') : digits;
  const n = parseInt(full, HEX_RADIX);
  if (full.length !== 6 || Number.isNaN(n)) {
    return [GREY, GREY, GREY];
  }
  return [((n >> 16) & BYTE) / BYTE, ((n >> 8) & BYTE) / BYTE, (n & BYTE) / BYTE];
}

/** sRGB colour of a scalar value 0..1 across evenly spaced stops (Minimap.tsx lensColour). */
function scalarColour(stops: readonly Rgb[], v: number): Rgb {
  if (stops.length === 1) {
    return stops[0];
  }
  const t = Math.min(1, Math.max(0, v)) * (stops.length - 1);
  const i = Math.min(stops.length - 2, Math.floor(t));
  const f = t - i;
  const a = stops[i];
  const b = stops[i + 1];
  return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, a[2] + (b[2] - a[2]) * f];
}

/** sRGB colour a lens byte stands for, or null when it has none. */
export function lensByteColour(lens: LensResult, stops: readonly Rgb[], byte: number): Rgb | null {
  if (byte === NO_DATA || stops.length === 0) {
    return null;
  }
  if (lens.kind === 'category') {
    return stops[byte] ?? null;
  }
  return scalarColour(stops, byte / SCALAR_MAX);
}

/** The 256-entry RGBA table (sRGB bytes, alpha 255 where an entry has a colour). Null lens: empty. */
export function buildLensLut(lens: LensResult | null): Uint8Array {
  const lut = new Uint8Array(LUT_SIZE * CHANNELS);
  if (!lens) {
    return lut;
  }
  const stops = lens.legend.map((l) => parseHex(l.color));
  for (let byte = 0; byte < LUT_SIZE; byte++) {
    const c = lensByteColour(lens, stops, byte);
    if (!c) {
      continue;
    }
    lut.set([Math.round(c[0] * BYTE), Math.round(c[1] * BYTE), Math.round(c[2] * BYTE), BYTE], byte * CHANNELS);
  }
  return lut;
}

/**
 * Lens value per hex as a byte, encoded exactly as hexdata.ts setLens (scalar = round(v · 254),
 * category = index, 255 = none). The lens layer keeps its own copy so a lens fades out over the
 * colours it had, instead of turning to "no data" the moment the view clears it.
 */
export function lensBytes(lens: LensResult): Uint8Array {
  const out = new Uint8Array(HEX_COUNT).fill(NO_DATA);
  for (let i = 0; i < HEX_COUNT; i++) {
    const v = lens.values[i];
    if (Number.isNaN(v)) {
      continue;
    }
    out[i] = lens.kind === 'category' ? Math.min(SCALAR_MAX, Math.round(v)) : Math.round(Math.min(1, Math.max(0, v)) * SCALAR_MAX);
  }
  return out;
}
